import { useQuery } from '@tanstack/react-query';
import { useTenant } from '@/services/tenant-context';
import {
  getBotUsage,
  getConversation,
  getCustomer,
  getDashboardSummary,
  getLead,
  getSupportRequest,
  getTicket,
  listBotKnowledge,
  listChannels,
  listConversationMessages,
  listConversations,
  listCustomers,
  listLeadActivity,
  listLeads,
  listSupportRequests,
  listTickets,
} from '@/services/engage-api';

/**
 * Every query key is scoped by tenant id so switching tenants resets the
 * cache. Mock-mode reads from the same hooks, so swapping to a real backend
 * needs no consumer changes.
 */

function tenantScope() {
  const { currentTenant } = useTenant();
  return currentTenant.id;
}

export function useDashboardSummary() {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['dashboard', 'summary', tenant],
    queryFn: getDashboardSummary,
  });
}

export function useLeads() {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['leads', tenant],
    queryFn: listLeads,
  });
}

export function useLead(leadId: string | undefined) {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['leads', tenant, leadId],
    queryFn: () => getLead(leadId!),
    enabled: Boolean(leadId),
  });
}

export function useLeadActivity(leadId: string | undefined) {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['leads', tenant, leadId, 'activity'],
    queryFn: () => listLeadActivity(leadId!),
    enabled: Boolean(leadId),
  });
}

export function useCustomers() {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['customers', tenant],
    queryFn: listCustomers,
  });
}

export function useCustomer(customerId: string | undefined) {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['customers', tenant, customerId],
    queryFn: () => getCustomer(customerId!),
    enabled: Boolean(customerId),
  });
}

/**
 * Per-call options consumers can pass to opt into behaviours that don't make
 * sense to enable globally — e.g. background polling, which we only want on
 * the actual list page the agent is staring at (not the Dashboard counts or
 * sidebar badges, which already refetch on focus).
 */
interface ListQueryOptions {
  refetchInterval?: number;
}

export function useConversations(options: ListQueryOptions = {}) {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['conversations', tenant],
    queryFn: listConversations,
    refetchInterval: options.refetchInterval,
  });
}

export function useConversation(conversationId: string | undefined) {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['conversations', tenant, conversationId],
    queryFn: () => getConversation(conversationId!),
    enabled: Boolean(conversationId),
  });
}

export function useConversationMessages(conversationId: string | undefined) {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['conversations', tenant, conversationId, 'messages'],
    queryFn: () => listConversationMessages(conversationId!),
    enabled: Boolean(conversationId),
  });
}

export function useSupportRequests(options: ListQueryOptions = {}) {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['support', tenant],
    queryFn: listSupportRequests,
    refetchInterval: options.refetchInterval,
  });
}

export function useSupportRequest(requestId: string | undefined) {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['support', tenant, requestId],
    queryFn: () => getSupportRequest(requestId!),
    enabled: Boolean(requestId),
  });
}

export function useTickets() {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['tickets', tenant],
    queryFn: listTickets,
  });
}

export function useTicket(ticketId: string | undefined) {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['tickets', tenant, ticketId],
    queryFn: () => getTicket(ticketId!),
    enabled: Boolean(ticketId),
  });
}

export function useChannels() {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['channels', tenant],
    queryFn: listChannels,
  });
}

export function useBotKnowledge() {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['bot-knowledge', tenant],
    queryFn: listBotKnowledge,
  });
}

export function useBotUsage() {
  const tenant = tenantScope();
  return useQuery({
    queryKey: ['bot-usage', tenant],
    queryFn: getBotUsage,
    // The strip + banners should reflect the latest usage when the tenant
    // comes back to the tab; usage drifts slowly so this is cheap.
    refetchOnWindowFocus: true,
  });
}
